import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, Droplets, ArrowRight } from 'lucide-react';

const imcClassifications = [
  { max: 18.5, label: 'Abaixo do peso', color: '#3B82F6', message: 'Seu IMC indica que você está abaixo do peso ideal. Uma avaliação nutricional pode ajudar a identificar estratégias para alcançar um peso saudável de forma equilibrada.' },
  { max: 24.9, label: 'Peso adequado', color: '#11830f', message: 'Seu IMC indica peso adequado. Isso sugere um equilíbrio saudável entre peso e altura. Para uma avaliação mais completa da sua composição corporal, o ideal é realizar uma consulta nutricional.' },
  { max: 29.9, label: 'Sobrepeso', color: '#F59E0B', message: 'Seu IMC indica sobrepeso. Com orientação nutricional adequada, é possível alcançar um peso mais saudável e melhorar sua qualidade de vida.' },
  { max: 34.9, label: 'Obesidade grau I', color: '#F97316', message: 'Seu IMC indica obesidade grau I. Um acompanhamento nutricional personalizado pode fazer toda a diferença na sua jornada rumo a uma vida mais saudável.' },
  { max: 39.9, label: 'Obesidade grau II', color: '#EF4444', message: 'Seu IMC indica obesidade grau II. É importante buscar acompanhamento profissional para desenvolver um plano alimentar seguro e eficaz.' },
  { max: Infinity, label: 'Obesidade grau III', color: '#DC2626', message: 'Seu IMC indica obesidade grau III. Procure acompanhamento nutricional e médico para iniciar uma mudança segura e gradual.' }
];

const getClassification = (imc) => imcClassifications.find(c => imc < c.max) || imcClassifications[imcClassifications.length - 1];

const IMCCalculator = () => {
  const [peso, setPeso] = useState('');
  const [altura, setAltura] = useState('');
  const [result, setResult] = useState(null);

  const calculate = () => {
    const p = parseFloat(peso);
    const a = parseFloat(altura) / 100;
    if (!p || !a || p <= 0 || a <= 0) return;
    const imc = p / (a * a);
    const classification = getClassification(imc);
    setResult({ value: imc.toFixed(1), ...classification });
  };

  return (
    <div className="bg-white rounded-3xl shadow-lg p-8 md:p-10 border border-gray-100" data-testid="imc-calculator">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-brand-green/10 flex items-center justify-center">
          <Calculator className="w-6 h-6 text-brand-green" />
        </div>
        <h3 className="text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Playfair Display, serif' }}>
          Calcule seu IMC
        </h3>
      </div>
      <p className="text-gray-500 mb-8 text-sm md:text-base leading-relaxed">
        Descubra rapidamente seu Índice de Massa Corporal e entenda em qual faixa você se encontra.
      </p>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-2">Peso (kg)</label>
          <input
            type="number"
            value={peso}
            onChange={(e) => setPeso(e.target.value)}
            placeholder="Ex: 70"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 outline-none transition-all text-[#1A1A1A]"
            data-testid="imc-peso-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-2">Altura (cm)</label>
          <input
            type="number"
            value={altura}
            onChange={(e) => setAltura(e.target.value)}
            placeholder="Ex: 175"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 outline-none transition-all text-[#1A1A1A]"
            data-testid="imc-altura-input"
          />
        </div>
      </div>

      <button
        onClick={calculate}
        className="w-full bg-brand-green text-white py-3.5 rounded-xl font-semibold text-base hover:bg-[#0d6a0c] transition-all shadow-md hover:shadow-lg"
        data-testid="imc-calculate-button"
      >
        Calcular IMC
      </button>

      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 20, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            transition={{ duration: 0.4 }}
            className="mt-8"
          >
            <div className="rounded-2xl p-6 text-center" style={{ backgroundColor: `${result.color}10`, border: `2px solid ${result.color}20` }}>
              <p className="text-sm text-gray-500 mb-1">Seu IMC é</p>
              <p className="text-5xl font-bold mb-2" style={{ color: result.color }} data-testid="imc-result-value">
                {result.value}
              </p>
              <span
                className="inline-block px-4 py-1.5 rounded-full text-sm font-semibold text-white mb-4"
                style={{ backgroundColor: result.color }}
                data-testid="imc-result-label"
              >
                {result.label}
              </span>
              <p className="text-sm text-gray-600 leading-relaxed">{result.message}</p>
            </div>
            <p className="text-xs text-gray-400 mt-4 text-center leading-relaxed">
              O IMC é apenas um indicador inicial. Para entender sua composição corporal de forma completa, agende uma avaliação nutricional.
            </p>
            <button
              onClick={() => document.getElementById('contato')?.scrollIntoView({ behavior: 'smooth' })}
              className="w-full mt-4 bg-brand-green/10 text-brand-green py-3 rounded-xl font-semibold text-sm hover:bg-brand-green hover:text-white transition-all border border-brand-green/20 inline-flex items-center justify-center gap-2"
              data-testid="imc-cta-button"
            >
              Quero uma avaliação nutricional completa
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const WaterCalculator = () => {
  const [peso, setPeso] = useState('');
  const [result, setResult] = useState(null);

  const calculate = () => {
    const p = parseFloat(peso);
    if (!p || p <= 0) return;
    const ml = p * 35;
    const liters = (ml / 1000).toFixed(1);
    const glasses = Math.round(ml / 250);
    setResult({ ml, liters, glasses });
  };

  return (
    <div className="bg-white rounded-3xl shadow-lg p-8 md:p-10 border border-gray-100" data-testid="water-calculator">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-brand-green/10 flex items-center justify-center">
          <Droplets className="w-6 h-6 text-brand-green" />
        </div>
        <h3 className="text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Playfair Display, serif' }}>
          Calculadora de Água
        </h3>
      </div>
      <p className="text-gray-500 mb-8 text-sm md:text-base leading-relaxed">
        Descubra a quantidade ideal de água que você deve consumir diariamente com base no seu peso.
      </p>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-600 mb-2">Peso (kg)</label>
        <input
          type="number"
          value={peso}
          onChange={(e) => setPeso(e.target.value)}
          placeholder="Ex: 70"
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 outline-none transition-all text-[#1A1A1A]"
          data-testid="water-peso-input"
        />
      </div>

      <button
        onClick={calculate}
        className="w-full bg-brand-green text-white py-3.5 rounded-xl font-semibold text-base hover:bg-[#0d6a0c] transition-all shadow-md hover:shadow-lg"
        data-testid="water-calculate-button"
      >
        Calcular
      </button>

      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 20, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            transition={{ duration: 0.4 }}
            className="mt-8"
          >
            <div className="rounded-2xl p-6 text-center bg-brand-green/5 border-2 border-brand-green/10">
              <p className="text-sm text-gray-500 mb-1">Você deve beber aproximadamente</p>
              <div className="flex items-baseline justify-center gap-1 mb-1">
                <p className="text-5xl font-bold text-brand-green" data-testid="water-result-value">{result.liters}</p>
                <span className="text-xl font-semibold text-brand-green/70">litros/dia</span>
              </div>
              <p className="text-sm text-gray-500 mb-4" data-testid="water-result-glasses">
                Aproximadamente {result.glasses} copos de 250ml
              </p>

              <div className="flex justify-center gap-1 mb-4">
                {[...Array(Math.min(result.glasses, 10))].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.08 }}
                  >
                    <Droplets className="w-5 h-5 text-brand-green" />
                  </motion.div>
                ))}
                {result.glasses > 10 && <span className="text-brand-green text-sm font-medium self-center">...</span>}
              </div>

              <p className="text-sm text-gray-600 leading-relaxed">
                A hidratação adequada é fundamental para o funcionamento do organismo, auxiliando no metabolismo, na digestão, na regulação da temperatura corporal e na saúde geral.
              </p>
            </div>
            <p className="text-xs text-gray-400 mt-4 text-center leading-relaxed">
              Este cálculo é apenas uma estimativa geral. As necessidades individuais podem variar de acordo com nível de atividade física, clima e condições de saúde.
            </p>
            <button
              onClick={() => document.getElementById('contato')?.scrollIntoView({ behavior: 'smooth' })}
              className="w-full mt-4 bg-brand-green/10 text-brand-green py-3 rounded-xl font-semibold text-sm hover:bg-brand-green hover:text-white transition-all border border-brand-green/20 inline-flex items-center justify-center gap-2"
              data-testid="water-cta-button"
            >
              Quero uma avaliação nutricional completa
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Calculators = () => {
  return (
    <section className="section-spacing bg-[#F2F0E9] relative overflow-hidden" id="calculadoras" data-testid="calculators-section">
      {/* Soft radial glow accents */}
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-[0.07]" style={{
        background: 'radial-gradient(circle at center, #11830f 0%, transparent 65%)'
      }} />
      <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full opacity-[0.05]" style={{
        background: 'radial-gradient(circle at center, #11830f 0%, transparent 60%)'
      }} />
      {/* Dot grid pattern */}
      <div className="absolute inset-0 opacity-[0.035]" style={{
        backgroundImage: 'radial-gradient(circle, #11830f 1px, transparent 1px)',
        backgroundSize: '32px 32px'
      }} />
      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12">
        <motion.div
          className="text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1A1A1A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ferramentas de <span className="text-brand-green">Saúde</span>
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-xl mx-auto leading-relaxed">
            Utilize nossas calculadoras para ter uma visão inicial sobre sua saúde e bem-estar.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <IMCCalculator />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <WaterCalculator />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Calculators;
